import { ReactNode } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'

export interface StepConfig {
  id: string
  title: string
  content: ReactNode
  canProceed?: boolean
  hideNext?: boolean
  hidePrevious?: boolean
  nextText?: string
  previousText?: string
}

interface MultistepDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  steps: StepConfig[]
  currentStep: number
  onStepChange: (step: number) => void
  onComplete?: () => void
  onCancel?: () => void
  className?: string
}

export function MultistepDialog({
  open,
  onOpenChange,
  steps,
  currentStep,
  onStepChange,
  onComplete,
  onCancel,
  className,
}: MultistepDialogProps) {
  const step = steps[currentStep]
  const isFirstStep = currentStep === 0
  const isLastStep = currentStep === steps.length - 1

  const handleNext = () => {
    if (isLastStep) {
      onComplete?.()
    } else {
      onStepChange(currentStep + 1)
    }
  }

  const handlePrevious = () => {
    if (isFirstStep) {
      onCancel?.()
    } else {
      onStepChange(currentStep - 1)
    }
  }

  const handleCancel = () => {
    onCancel?.()
    onOpenChange(false)
  }

  if (!step) return null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={cn('max-w-2xl p-8 block', className)}>
        <DialogHeader>
          <DialogTitle>{step.title}</DialogTitle>
        </DialogHeader>

        {/* Step content */}
        <div className="min-h-[200px] mb-6">{step.content}</div>

        <DialogFooter className="flex items-center justify-between">
          <div>
            {!isFirstStep && !step.hidePrevious && (
              <Button
                variant="outline"
                onClick={handlePrevious}
                className="flex items-center space-x-2"
              >
                <ChevronLeft className="h-4 w-4" />
                <span>{step.previousText || 'Previous'}</span>
              </Button>
            )}
          </div>

          <div className="flex space-x-2">
            <Button variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            {!step.hideNext && (
              <Button
                onClick={handleNext}
                disabled={step.canProceed === false}
                className="flex items-center space-x-2"
              >
                <span>
                  {step.nextText || (isLastStep ? 'Complete' : 'Next')}
                </span>
                {!isLastStep && <ChevronRight className="h-4 w-4" />}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
